import React, { createRef, useEffect } from "react";
import { useState } from "react";
import { connect } from "react-redux";
import * as cn from "classnames/bind";
import Button from "content/button/button";
import * as styles from "./owner-page-image-uploader.css";
import { actionCreators, getUploadedOwnerPageImages } from "ui/store";
import { CustomImage, OnSaveResponse, OwnerPageInfo } from "burde";
import { getResource } from "resource/store";
import { ApplicationState } from "store";
import ZentaImagePicker from "content/image/zenta-image-picker";
import { getCustomImageById } from "calendar/service";

type ImageUploaderProps = {
    onSaveCallback: (
        blob: Blob,
        calendarSize: string,
    ) => Promise<OnSaveResponse>;
    onSelectCallback: (url: string | null) => Promise<OnSaveResponse>;
    ownerPage: OwnerPageInfo;
    Cover_Image_UploadImage: string;
    Cover_Image_EditImage: string;
    uploadedOwnerPageImages: Array<CustomImage>;
    onRemoveCallback: (imageUrl: string) => void;
} & typeof actionCreators;

const cx = cn.bind(styles);

const buttonCn = () =>
    cx({
        uploadButton: true,
    });

const ImageUploader: React.FC<ImageUploaderProps> = ({
    UpdateImageCropper,
    onSaveCallback,
    onSelectCallback,
    ownerPage,
    Cover_Image_UploadImage,
    Cover_Image_EditImage,
    uploadedOwnerPageImages,
    onRemoveCallback,
    updateUploadedOwnerPageImages,
}) => {
    const fileInput = createRef<HTMLInputElement>();
    const [selectedImages, setSelectedImages] = useState<CustomImage[]>([]);
    const [selectedImagesSet, setSelectedImagesSet] = useState<boolean>(false);

    const uploadBackgroundImage = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const { files } = e.target;
        const file = files && files.length > 0 ? files[0] : null;
        if (file === null) {
            return;
        }
        const imageUrl = window.URL.createObjectURL(file);
        let image = new Image();
        image.src = imageUrl;

        if (imageUrl) {
            UpdateImageCropper({
                isVisible: true,
                imageUrl,
                onSaveCallback,
            });
        }

        e.target.value = "";
    };

    const editUploadedImage = () => {
        if (ownerPage.customImageId) {
            const found = uploadedOwnerPageImages.find(
                (x) => x.id === ownerPage.customImageId,
            );

            if (found) {
                UpdateImageCropper({
                    isVisible: true,
                    imageUrl: found.url,
                    onSaveCallback,
                });
            }
        }
    };

    useEffect(() => {
        getAndSetSelectedImage();
    }, [ownerPage.customImageId]);

    const getAndSetSelectedImage = async () => {
        if (ownerPage.customImageId) {
            const ownerPageImage = await getCustomImageById(
                ownerPage.customImageId,
            );
            if (ownerPageImage) {
                setSelectedImages([ownerPageImage]);
                if (
                    !uploadedOwnerPageImages.find(
                        (x) => x.id === ownerPage.customImageId,
                    )
                ) {
                    updateUploadedOwnerPageImages([
                        ...uploadedOwnerPageImages,
                        ownerPageImage,
                    ]);
                }
            }
        } else {
            setSelectedImages([]);
        }

        setSelectedImagesSet(true);
    };

    const getSelectedImageUrl = () => {
        if (ownerPage.customImageId) {
            const found = uploadedOwnerPageImages.find(
                (x) => x.id === ownerPage.customImageId,
            );

            if (found) {
                return found.url;
            }
        }

        return null;
    };

    const getImagePickerImages = () => {
        const images = [...uploadedOwnerPageImages].map((x) => x.url);
        return images;
    };

    const imageIsEditable = () => {
        if (ownerPage.customImageId) {
            const found = uploadedOwnerPageImages.find(
                (x) => x.id === ownerPage.customImageId,
            );

            if (found) {
                return true;
            }
        }

        return false;
    };

    return (
        <React.Fragment>
            <input
                style={{ display: "none" }}
                ref={fileInput}
                type="file"
                onChange={uploadBackgroundImage}
                accept={"image/*"}
            />

            <Button
                onClick={() => {
                    fileInput.current ? fileInput.current.click() : "";
                }}
                className={buttonCn()}
                text={Cover_Image_UploadImage}
            />
            {selectedImagesSet && (
                <ZentaImagePicker
                    selectedImage={getSelectedImageUrl()}
                    images={getImagePickerImages()}
                    defaultRoute={""}
                    onClick={(selectedImage) => {
                        onSelectCallback(selectedImage);
                    }}
                    onRemove={(selectedImage) => {
                        onRemoveCallback(selectedImage);
                    }}
                    resizeImage={false}
                    selectedImages={selectedImages}
                />
            )}

            {imageIsEditable() && (
                <Button
                    onClick={() => {
                        editUploadedImage();
                    }}
                    className={buttonCn()}
                    text={Cover_Image_EditImage}
                />
            )}
        </React.Fragment>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    Cover_Image_UploadImage: getResource(state, {
        key: "Cover_Image_UploadImage",
    }),
    Cover_Image_EditImage: getResource(state, {
        key: "Cover_Image_EditImage",
    }),
    uploadedOwnerPageImages: getUploadedOwnerPageImages(state),
});

const mapDispatchToProps = {
    ...actionCreators,
};

const ConnectedImageUploader = connect(
    mapStateToProps,
    mapDispatchToProps,
)(ImageUploader);

export { ConnectedImageUploader };
